<template>
  <div class="quiz-container min-h-screen relative overflow-hidden" :style="backgroundStyle">
    <!-- Gradient overlay for readability -->
    <div class="quiz-overlay absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>

    <!-- Difficulty Selection Screen -->
    <div v-if="!selectedDifficulty && !quizData && !loading && !error" class="difficulty-screen relative z-10 flex items-center justify-center min-h-screen px-4">
      <div class="difficulty-content bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
        <div class="text-center mb-8">
          <i class="fas fa-graduation-cap text-6xl text-purple-600 mb-4"></i>
          <h1 class="text-3xl md:text-4xl font-bold text-gray-800 mb-3">Choose Your Challenge</h1>
          <p class="text-gray-600 text-lg">Select a difficulty level to start the quiz</p>
        </div>

        <div class="difficulty-options grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <button
            @click="selectDifficulty('easy')"
            class="difficulty-option bg-gradient-to-br from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 text-white p-6 rounded-xl transition-all transform hover:-translate-y-1 hover:shadow-xl"
          >
            <div class="text-4xl mb-3">🌱</div>
            <h3 class="text-xl font-bold mb-2">Easy</h3>
            <p class="text-sm opacity-90">5 questions</p>
            <p class="text-xs opacity-75 mt-2">Simple & straightforward</p>
          </button>

          <button
            @click="selectDifficulty('medium')"
            class="difficulty-option bg-gradient-to-br from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white p-6 rounded-xl transition-all transform hover:-translate-y-1 hover:shadow-xl"
          >
            <div class="text-4xl mb-3">⭐</div>
            <h3 class="text-xl font-bold mb-2">Medium</h3>
            <p class="text-sm opacity-90">5 questions</p>
            <p class="text-xs opacity-75 mt-2">More challenging topics</p>
          </button>

          <button
            @click="selectDifficulty('hard')"
            class="difficulty-option bg-gradient-to-br from-red-500 to-purple-600 hover:from-red-600 hover:to-purple-700 text-white p-6 rounded-xl transition-all transform hover:-translate-y-1 hover:shadow-xl"
          >
            <div class="text-4xl mb-3">🔥</div>
            <h3 class="text-xl font-bold mb-2">Hard</h3>
            <p class="text-sm opacity-90">5 questions</p>
            <p class="text-xs opacity-75 mt-2">Tricky & detailed!</p>
          </button>
        </div>

        <button
          @click="returnToMap"
          class="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 px-6 rounded-xl font-bold transition-all"
        >
          <i class="fas fa-arrow-left mr-2"></i>Back
        </button>
      </div>
    </div>

    <!-- Quiz Preparation Loading Screen -->
    <div v-if="preparingQuiz" class="loading-screen relative z-10 flex items-center justify-center min-h-screen px-4">
      <div class="loading-content bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-md text-center">
        <div class="captain-leo-container mb-6">
          <img src="@/assets/logo.png" alt="Captain Leo" class="w-32 h-32 mx-auto animate-bounce" />
        </div>
        <h2 class="text-2xl md:text-3xl font-bold text-purple-600 mb-3">Preparing Your Quiz...</h2>
        <p class="text-gray-600 text-lg mb-4">Captain Leo is getting everything ready for you!</p>
        <div class="loading-dots flex gap-2 justify-center">
          <div class="dot bg-purple-500 w-3 h-3 rounded-full animate-pulse"></div>
          <div class="dot bg-purple-500 w-3 h-3 rounded-full animate-pulse" style="animation-delay: 0.2s;"></div>
          <div class="dot bg-purple-500 w-3 h-3 rounded-full animate-pulse" style="animation-delay: 0.4s;"></div>
        </div>
      </div>
    </div>

    <!-- Loading State (fallback) -->
    <div v-else-if="loading && !quizData" class="loading-screen relative z-10 flex items-center justify-center min-h-screen">
      <div class="loading-content text-center text-white">
        <div class="spinner border-8 border-white/30 border-t-white rounded-full w-20 h-20 animate-spin mx-auto mb-4"></div>
        <p class="text-xl font-semibold">{{ loadingMessage }}</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-screen relative z-10 flex items-center justify-center min-h-screen px-4">
      <div class="error-content bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
        <i class="fas fa-exclamation-circle text-6xl text-red-500 mb-4"></i>
        <h2 class="text-2xl font-bold text-gray-800 mb-3">Oops! Something went wrong</h2>
        <p class="text-gray-600 mb-6">{{ error }}</p>
        <div class="flex gap-3">
          <button @click="resetQuiz" class="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-xl font-bold transition-all">
            <i class="fas fa-redo mr-2"></i>Try Again
          </button>
          <button @click="returnToMap" class="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl font-bold transition-all">
            <i class="fas fa-home mr-2"></i>Home
          </button>
        </div>
      </div>
    </div>

    <!-- Main Quiz Content -->
    <div v-else-if="quizData && quizReady" class="quiz-content relative z-10 min-h-screen flex flex-col p-4 md:p-8">
      <!-- Header Section -->
      <div class="quiz-header flex justify-between items-center mb-4">
        <button
          @click="handleExit"
          class="exit-btn bg-white/90 hover:bg-white text-gray-800 py-2 px-4 md:py-3 md:px-6 rounded-xl font-bold shadow-lg transition-all hover:shadow-xl flex items-center gap-2"
        >
          <i class="fas fa-times"></i>
          <span class="hidden md:inline">Exit</span>
        </button>

        <div class="text-white text-sm md:text-base font-semibold">
          Question {{ currentQuestionIndex + 1 }} of {{ totalQuestions }}
        </div>
      </div>

      <!-- Progress Bar Track -->
      <div class="progress-bar-container mb-6 px-4">
        <div class="progress-bar-wrapper relative">
          <!-- Background Track -->
          <div class="progress-bar-track bg-white/20 h-2 rounded-full overflow-hidden">
            <!-- Filled Progress -->
            <div
              class="progress-bar-fill bg-gradient-to-r from-purple-500 to-indigo-500 h-full transition-all duration-500 ease-out"
              :style="{ width: progressPercentage + '%' }"
            ></div>
          </div>

          <!-- Question Number Indicators -->
          <div class="progress-indicators absolute top-0 left-0 right-0 flex justify-between items-center" style="transform: translateY(-50%);">
            <div
              v-for="index in totalQuestions"
              :key="index"
              class="progress-dot w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 shadow-lg"
              :class="getQuestionStatusClass(index - 1)"
            >
              {{ index }}
            </div>
          </div>
        </div>
      </div>

      <!-- Quiz Title -->
      <div class="quiz-title-section text-center mb-6">
        <h1 class="quiz-title text-2xl md:text-4xl font-bold text-white drop-shadow-lg">
          {{ quizData.title }}
        </h1>
        <p class="quiz-difficulty text-white/90 text-sm md:text-base mt-2">
          <span class="inline-block px-3 py-1 bg-white/20 rounded-full backdrop-blur-sm">
            {{ difficultyLabel }} • {{ quizData.question_count }} Questions
          </span>
        </p>
      </div>

      <!-- Captain Leo Mascot (Desktop - above question) -->
      <div class="mascot-section hidden lg:flex justify-start items-center gap-3 mb-4 px-4">
        <img src="@/assets/leo-binoculars.png" alt="Captain Leo" class="mascot-image w-20 h-20 object-contain" />
        <div class="speech-bubble relative bg-white rounded-2xl shadow-xl p-4 max-w-md">
          <p class="text-gray-800 font-medium">{{ mascotMessage }}</p>
          <div class="speech-arrow absolute left-0 top-1/2 transform -translate-x-full -translate-y-1/2">
            <div class="w-0 h-0 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-white"></div>
          </div>
        </div>
      </div>

      <!-- Mobile Mascot (inline) -->
      <div class="mascot-mobile lg:hidden mb-6">
        <div class="speech-bubble bg-white rounded-xl shadow-lg p-4 text-center">
          <div class="flex items-center justify-center gap-3">
            <img src="@/assets/leo-binoculars.png" alt="Captain Leo" class="w-12 h-12 object-contain" />
            <p class="text-gray-800 font-medium flex-1 text-left">{{ mascotMessage }}</p>
          </div>
        </div>
      </div>

      <!-- Question Section -->
      <div class="question-section flex-1 flex items-center justify-center mb-6">
        <QuestionCard
          v-if="!quizResult && !submitting && currentQuestion"
          :question="currentQuestion"
          :feedback="feedbackState"
          :disabled="submitting"
          @answer-selected="handleAnswerSelect"
        />
      </div>

      <!-- Navigation -->
      <div v-if="feedbackState && !quizResult && !submitting" class="navigation-section text-center mb-6">
        <button
          @click="nextQuestion"
          class="next-btn bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-4 px-8 md:px-12 rounded-2xl font-bold text-lg md:text-xl shadow-2xl hover:shadow-3xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3 mx-auto"
        >
          {{ isLastQuestion ? 'Finish Quiz' : 'Next Question' }}
          <i class="fas fa-arrow-right"></i>
        </button>
      </div>

      <!-- Processing Results Loading Screen -->
      <div v-if="submitting && !quizResult" class="processing-screen flex-1 flex items-center justify-center">
        <div class="processing-content bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-md text-center">
          <div class="captain-leo-container mb-6 relative">
            <img src="@/assets/logo.png" alt="Captain Leo" class="w-32 h-32 mx-auto animate-bounce" />
            <div class="spinner-ring absolute inset-0 flex items-center justify-center">
              <div class="ring"></div>
            </div>
          </div>
          <h2 class="text-2xl md:text-3xl font-bold text-purple-600 mb-3">Calculating Results...</h2>
          <p class="text-gray-600 text-lg mb-4">Captain Leo is tallying your score and checking for achievements!</p>
          <div class="loading-dots flex gap-2 justify-center">
            <div class="dot bg-purple-500 w-3 h-3 rounded-full animate-pulse"></div>
            <div class="dot bg-purple-500 w-3 h-3 rounded-full animate-pulse" style="animation-delay: 0.2s;"></div>
            <div class="dot bg-purple-500 w-3 h-3 rounded-full animate-pulse" style="animation-delay: 0.4s;"></div>
          </div>
        </div>
      </div>

      <!-- Result Screen -->
      <div v-if="quizResult" class="result-section flex-1 flex items-center justify-center">
        <ResultScreen
          :result="quizResult"
          :quiz-title="quizData.title"
          :related-slug="relatedSlug"
          :user-answers="userAnswers"
          :questions="quizData.questions"
          @try-again="tryAgain"
          @return-to-map="returnToMap"
          @view-leaderboards="viewLeaderboards"
          @return-to-source="returnToSource"
        />
      </div>
    </div>

    <!-- Achievement Popups -->
    <AchievementPopup
      v-if="currentAchievement"
      :achievement="currentAchievement"
      @close="dismissAchievement"
    />

    <!-- Exit Modal -->
    <ExitModal
      v-if="showExitModal"
      @confirm="confirmExit"
      @cancel="showExitModal = false"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import QuestionCard from '@/components/quiz/QuestionCard.vue'
import ResultScreen from '@/components/quiz/ResultScreen.vue'
import AchievementPopup from '@/components/quiz/AchievementPopup.vue'
import ExitModal from '@/components/quiz/ExitModal.vue'

export default {
  name: 'QuizView',
  components: {
    QuestionCard,
    ResultScreen,
    AchievementPopup,
    ExitModal
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      quizId: null,
      quizData: null,
      selectedDifficulty: null,
      quizType: null,
      referenceId: null,
      currentQuestionIndex: 0,
      userAnswers: [],
      feedbackState: null,
      loading: false,
      submitting: false,
      quizResult: null,
      relatedSlug: null,
      showExitModal: false,
      backgroundImage: null,
      quizStartTime: null,
      questionStartTime: null,
      achievementQueue: [],
      currentAchievement: null,
      error: null,
      preparingQuiz: false,
      quizReady: false
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'user']),

    currentQuestion() {
      if (!this.quizData || !this.quizData.questions) return null
      return this.quizData.questions[this.currentQuestionIndex]
    },

    totalQuestions() {
      return this.quizData?.question_count || 0
    },

    isLastQuestion() {
      return this.currentQuestionIndex === this.totalQuestions - 1
    },

    progressPercentage() {
      if (this.totalQuestions === 0) return 0
      // Progress based on answered questions
      const answeredCount = this.userAnswers.length
      return Math.floor((answeredCount / this.totalQuestions) * 100)
    },

    backgroundStyle() {
      if (this.backgroundImage) {
        return {
          backgroundImage: `url(${this.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }
      }
      return {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }
    },

    difficultyLabel() {
      const difficulty = this.quizData?.difficulty || 'medium'
      return difficulty.charAt(0).toUpperCase() + difficulty.slice(1)
    },

    mascotMessage() {
      // Initial message
      if (this.currentQuestionIndex === 0 && !this.feedbackState && !this.quizResult) {
        return `Let's explore ${this.quizData?.title || 'this quiz'}! 🌍`
      }

      // Feedback messages
      if (this.feedbackState) {
        if (this.feedbackState.isCorrect) {
          const messages = [
            "Amazing! You're a geography genius! 🌟",
            "Perfect! Captain Leo is impressed! ⭐",
            "Excellent work, explorer! 🎯",
            "You nailed it! Keep going! 🚀"
          ]
          return messages[Math.floor(Math.random() * messages.length)]
        } else {
          const messages = [
            "Don't worry, we learn from every adventure! 📚",
            "Good try! Let's keep exploring! 🗺️",
            "That's okay, you're doing great! 💪",
            "No problem! On to the next one! 🌈"
          ]
          return messages[Math.floor(Math.random() * messages.length)]
        }
      }

      // Progress messages
      if (this.currentQuestionIndex === Math.floor(this.totalQuestions / 2)) {
        return "You're halfway there! Keep it up! 🏁"
      }

      if (this.currentQuestionIndex === this.totalQuestions - 1) {
        return "Last question! Finish strong! 💪"
      }

      // Result message
      if (this.quizResult) {
        if (this.quizResult.is_perfect_score) {
          return "Perfect score! You're a legend! 🏆"
        } else if (this.quizResult.accuracy >= 80) {
          return "Excellent work, explorer! 🌟"
        } else {
          return "Great effort! Keep learning! 📚"
        }
      }

      return "What's your answer, explorer? 🤔"
    },

    loadingMessage() {
      if (this.quizData) {
        return 'Loading quiz...'
      }
      return 'Generating your quiz...'
    }
  },
  async mounted() {
    // Parse the id to extract type and reference ID
    // Expected format: "article-{slug}" or "place-{slug}"
    this.parseQuizId()
  },
  beforeRouteLeave(to, from, next) {
    // If quiz completed or error state, allow navigation without confirmation
    if (this.quizResult || this.error) {
      next()
      return
    }

    // If no difficulty selected yet (still on difficulty selection screen), allow navigation
    if (!this.selectedDifficulty) {
      next()
      return
    }

    // If quiz in progress, show confirmation
    this.pendingNavigation = next
    this.showExitModal = true
    next(false)
  },
  methods: {
    /**
     * Parse the quiz ID to extract type and reference ID
     */
    parseQuizId() {
      try {
        // Expected format: "article-{slug}" or "place-{slug}"
        const parts = this.id.split('-')
        if (parts.length < 2) {
          throw new Error('Invalid quiz ID format')
        }

        this.quizType = parts[0]
        this.referenceId = parts.slice(1).join('-') // Handle slugs with dashes

        // Validate type
        if (!['article', 'place'].includes(this.quizType)) {
          throw new Error('Invalid quiz type')
        }
      } catch (err) {
        console.error('Error parsing quiz ID:', err)
        this.error = err.message || 'Invalid quiz format'
      }
    },

    /**
     * Handle difficulty selection
     */
    async selectDifficulty(difficulty) {
      this.selectedDifficulty = difficulty
      await this.generateQuiz()
    },

    /**
     * Generate/fetch quiz data from API
     */
    async generateQuiz() {
      try {
        this.preparingQuiz = true
        this.error = null

        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/quizzes/generate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              type: this.quizType,
              id: this.referenceId,
              difficulty: this.selectedDifficulty
            })
          }
        )

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to generate quiz')
        }

        const data = await response.json()

        // Store quiz data
        this.quizData = data.quiz
        this.quizId = data.quiz.id
        this.relatedSlug = data.quiz.reference_id || this.referenceId

        // Load background image
        await this.loadBackgroundImage()

        // Show preparation screen for a moment before starting
        await new Promise(resolve => setTimeout(resolve, 1000))

        // Now mark quiz as ready and start the timer
        this.preparingQuiz = false
        this.quizReady = true
        this.quizStartTime = Date.now()
        this.questionStartTime = Date.now()
      } catch (err) {
        console.error('Error generating quiz:', err)
        this.error = err.message || 'Failed to generate quiz'
        this.preparingQuiz = false
      }
    },

    /**
     * Reset quiz to difficulty selection
     */
    resetQuiz() {
      this.selectedDifficulty = null
      this.quizData = null
      this.quizId = null
      this.currentQuestionIndex = 0
      this.userAnswers = []
      this.feedbackState = null
      this.quizResult = null
      this.error = null
      this.loading = false
      this.preparingQuiz = false
      this.quizReady = false
      this.submitting = false
    },

    /**
     * Load dynamic background image from quiz context
     */
    async loadBackgroundImage() {
      try {
        if (this.quizData.type === 'place') {
          const response = await fetch(
            `${process.env.VUE_APP_API_URL}/api/images?query=${encodeURIComponent(this.quizData.title)}&itemSlug=${this.quizData.reference_id}`
          )
          const images = await response.json()
          if (images && images.length > 0) {
            this.backgroundImage = images[0].url
          }
        } else if (this.quizData.type === 'article') {
          // Use relatedSlug as fallback if reference_id is not available
          const slug = this.quizData.reference_id || this.relatedSlug;

          if (!slug) {
            console.error('[QuizView] No slug available for background image');
            return;
          }

          const response = await fetch(
            `${process.env.VUE_APP_API_URL}/api/moreInfo?slug=${slug}`
          )
          const article = await response.json()

          if (article.images && article.images.length > 0) {
            const imageUrl = article.images[0].link || article.images[0].url || article.images[0];
            this.backgroundImage = imageUrl;
          }
        }
      } catch (error) {
        console.warn('Failed to load background image:', error)
        // Fallback to gradient
      }
    },

    /**
     * Handle answer selection
     */
    handleAnswerSelect(selectedOptionId) {
      const currentTime = Date.now()
      const timeSpent = Math.floor((currentTime - this.questionStartTime) / 1000)

      // Determine if answer is correct
      const isCorrect = this.currentQuestion.correct_option_id === selectedOptionId

      // Store answer
      this.userAnswers.push({
        question_id: this.currentQuestion.id,
        selected_option_id: selectedOptionId,
        time_spent: timeSpent,
        isCorrect: isCorrect
      })

      // Show feedback
      this.feedbackState = {
        isCorrect: isCorrect,
        selectedOptionId: selectedOptionId,
        correctOptionId: this.currentQuestion.correct_option_id
      }
    },

    /**
     * Move to next question or finish quiz
     */
    nextQuestion() {
      // Clear feedback
      this.feedbackState = null

      // Reset question timer
      this.questionStartTime = Date.now()

      // Check if last question
      if (this.isLastQuestion) {
        this.submitQuiz()
      } else {
        this.currentQuestionIndex++
      }
    },

    /**
     * Submit quiz answers to backend
     */
    async submitQuiz() {
      try {
        this.submitting = true

        const token = localStorage.getItem('auth-token')
        if (!token) {
          throw new Error('Authentication required')
        }

        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/quizzes/${this.quizId}/submit`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              answers: this.userAnswers.map(a => ({
                question_id: a.question_id,
                selected_option_id: a.selected_option_id,
                time_spent: a.time_spent
              }))
            })
          }
        )

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Submission failed')
        }

        const data = await response.json()
        this.quizResult = data.quiz_result

        // Display achievements
        if (data.new_achievements && data.new_achievements.length > 0) {
          this.queueAchievements(data.new_achievements)
        }

        this.submitting = false
      } catch (err) {
        console.error('Error submitting quiz:', err)
        this.error = err.message
        this.submitting = false
      }
    },

    /**
     * Queue achievements for sequential display
     */
    queueAchievements(achievements) {
      this.achievementQueue = [...achievements]
      this.displayNextAchievement()
    },

    /**
     * Display next achievement from queue
     */
    displayNextAchievement() {
      if (this.achievementQueue.length === 0) {
        this.currentAchievement = null
        return
      }

      this.currentAchievement = this.achievementQueue.shift()
    },

    /**
     * Dismiss current achievement and show next
     */
    dismissAchievement() {
      setTimeout(() => {
        this.displayNextAchievement()
      }, 300)
    },

    /**
     * Handle exit button click
     */
    handleExit() {
      this.showExitModal = true
    },

    /**
     * Confirm exit and navigate away
     * Clears error state before navigation
     */
    confirmExit() {
      this.showExitModal = false
      // Clear error state to ensure navigation isn't blocked
      this.error = null

      if (this.pendingNavigation) {
        // If navigation was triggered by route guard, use the pending callback
        this.pendingNavigation()
        this.pendingNavigation = null
      } else {
        // If navigation was triggered by Exit button, navigate to app manually
        // We need to bypass the route guard, so set a flag or navigate directly
        this.selectedDifficulty = null // Reset to bypass guard
        this.quizResult = { completed: true } // Mark as completed to bypass guard
        this.$router.push('/app').catch(err => {
          console.error('Navigation error:', err)
          window.location.href = '/app'
        })
      }
    },

    /**
     * Try quiz again
     */
    tryAgain() {
      window.location.reload()
    },

    /**
     * Return to map
     * Clears error state and navigates to home
     */
    returnToMap() {
      // Clear error state to ensure navigation isn't blocked
      this.error = null
      this.$router.push('/').catch(err => {
        // Handle navigation errors gracefully
        console.error('Navigation error:', err)
        // Force reload as fallback
        window.location.href = '/'
      })
    },

    /**
     * View leaderboards
     */
    viewLeaderboards() {
      this.$router.push('/leaderboards')
    },

    /**
     * Return to source article/place
     */
    returnToSource() {
      if (!this.relatedSlug) {
        this.returnToMap()
        return
      }

      if (this.quizData.type === 'place') {
        this.$router.push({ name: 'Location', params: { slug: this.relatedSlug } })
      } else if (this.quizData.type === 'article') {
        this.$router.push({ name: 'Article', params: { slug: this.relatedSlug } })
      } else {
        this.returnToMap()
      }
    },

    /**
     * Get CSS class for question status indicator
     */
    getQuestionStatusClass(questionIndex) {
      if (questionIndex < this.userAnswers.length) {
        // Question has been answered
        const answer = this.userAnswers[questionIndex]
        if (answer.isCorrect) {
          return 'bg-green-500 text-white border-2 border-green-600'
        } else {
          return 'bg-red-500 text-white border-2 border-red-600'
        }
      } else if (questionIndex === this.currentQuestionIndex) {
        // Current question
        return 'bg-yellow-400 text-purple-900 border-2 border-yellow-500 animate-pulse ring-2 ring-yellow-300'
      } else {
        // Not answered yet
        return 'bg-white/30 text-white border-2 border-white/40'
      }
    }
  }
}
</script>

<style scoped>
/* Loading spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spinner {
  animation: spin 1s linear infinite;
}

/* Processing results spinner ring */
.spinner-ring .ring {
  width: 140px;
  height: 140px;
  border: 4px solid rgba(147, 51, 234, 0.1);
  border-top-color: rgb(147, 51, 234);
  border-radius: 50%;
  animation: spin 1.5s linear infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.loading-dots .dot {
  animation: pulse 1.5s ease-in-out infinite;
}

/* Progress bar styles */
.progress-bar-wrapper {
  padding-top: 1rem;
  padding-bottom: 0.5rem;
}

.progress-bar-track {
  position: relative;
}

.progress-dot {
  transition: all 0.3s ease;
  z-index: 10;
}

/* Smooth transitions */
.quiz-container {
  transition: background 0.5s ease;
}

/* Speech bubble animations */
.speech-bubble {
  animation: bubble-appear 0.3s ease-out;
}

@keyframes bubble-appear {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Responsive mascot positioning */
@media (max-width: 1024px) {
  .mascot-section {
    display: none;
  }
}
</style>
